import { Editor } from "@tiptap/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useGenerateAnswerWithIA, { Tone } from "./useGenerateAnswerWithIA";
import { useToast } from "./utils/useToast";

interface originalSuggestion {
    original: string;
    suggestion: string;
    from: number;
    to: number;
    originalHTML: string;
}

interface UseSuggestionProps {
    editor: Editor | null;
    suggestionEditor: Editor | null;
    setPendingSuggestion: (pendingSuggestion: boolean) => void;
    mailContent?: string;
}

const useSuggestion = ({
    editor,
    suggestionEditor,
    setPendingSuggestion,
    mailContent,
}: UseSuggestionProps): {
    isLoading: boolean;
    isPendingChange: boolean;
    makeSuggestion: (action: Tone) => void;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
} => {
    const { generateAnswer, isLoading } = useGenerateAnswerWithIA();
    const { toast } = useToast();
    const { t } = useTranslation();
    const [isPendingChange, setIsPendingChange] = useState(false);
    const [originalSuggestion, setOriginalSuggestion] =
        useState<originalSuggestion | null>(null);

    const makeSuggestion = async (action: Tone) => {
        if (!editor || !suggestionEditor) {
            throw new Error(
                "Editor or suggestionEditor or mailContent not found",
            );
        }
        editor.setEditable(false);

        try {
            // The editor now holds only the user's message (signature and
            // forwarded thread live outside it — AB#1746), so no stripping is
            // needed before sending it to the AI.
            const originalHTML = editor.getHTML();

            let { to, from } = editor.state.selection;

            if (to === from) {
                editor.commands.selectAll();
                to = editor.state.selection.to - 1;
                from = editor.state.selection.from;
            }

            const originalSelection = editor.state.doc.textBetween(from, to);

            const AIsuggestion = await generateAnswer(
                originalSelection,
                originalHTML,
                mailContent || "",
                action,
            );

            if (!AIsuggestion) {
                toast({
                    title: t("errors.noSuggestion"),
                    variant: "destructive",
                });

                editor.setEditable(true);

                return;
            }

            editor.commands.setHighlight({
                color: "#ffcdd2",
            });

            suggestionEditor.commands.setContent("");
            suggestionEditor.commands.insertContent(originalHTML);
            suggestionEditor.commands.insertContentAt(
                { from, to },
                `<mark style="background-color: #c8e6c9;">${AIsuggestion}</mark>`,
            );

            setOriginalSuggestion({
                original: originalSelection,
                suggestion: AIsuggestion,
                from,
                to,
                originalHTML,
            });

            setPendingSuggestion(true);
            setIsPendingChange(true);
        } catch (error) {
            console.error(error);
            editor.setEditable(true);
        }
    };

    const acceptOrRefuseSuggestion = (accept: boolean) => {
        if (!editor || !suggestionEditor) {
            throw new Error("Editor or suggestionEditor not found");
        }

        if (!originalSuggestion) {
            return;
        }

        if (accept) {
            editor.commands.insertContentAt(
                { from: originalSuggestion.from, to: originalSuggestion.to },
                originalSuggestion.suggestion,
            );

            editor.commands.selectAll();
            editor.commands.unsetHighlight();
            editor.commands.setTextSelection(originalSuggestion.from);
            editor.commands.focus();
        } else {
            editor.commands.setContent(originalSuggestion.originalHTML);
        }

        setIsPendingChange(false);
        setPendingSuggestion(false);
        editor.setEditable(true);
    };

    return {
        isLoading,
        isPendingChange,
        makeSuggestion,
        acceptOrRefuseSuggestion,
    };
};

export default useSuggestion;
